import { useEffect, useState } from 'react';
import { supabase } from '../utils/supabase';
import { toast } from 'react-hot-toast';
import type { User } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';

type UserProfile = Database['public']['Tables']['users']['Row'];

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        await fetchProfile(session.user.id);
      } else {
        setProfile(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      if (data) {
        setProfile(data);
      }
    } catch (err: any) {
      console.error('Error fetching user profile:', err);
      setError(err.message);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      if (data.user) {
        await fetchProfile(data.user.id);
        toast.success('Successfully signed in');
      }
    } catch (err: any) {
      console.error('Sign in error:', err);
      setError(err.message);
      toast.error(err.message);
      throw err;
    }
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    try {
      setError(null);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName
          }
        }
      });

      if (error) throw error;
      if (data.user) {
        toast.success('Account created successfully! Please check your email to confirm your account.');
      }
    } catch (err: any) {
      console.error('Sign up error:', err);
      setError(err.message);
      toast.error(err.message);
      throw err;
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
      setProfile(null);
      toast.success('Successfully signed out');
    } catch (err: any) {
      console.error('Sign out error:', err);
      setError(err.message);
      toast.error(err.message);
      throw err;
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    try {
      if (!user) throw new Error('No user logged in');

      const { error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;
      await fetchProfile(user.id);
      toast.success('Profile updated successfully');
    } catch (err: any) {
      console.error('Profile update error:', err);
      setError(err.message);
      toast.error(err.message);
      throw err;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      if (error) throw error;
      toast.success('Password reset instructions sent to your email');
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(err.message);
      toast.error(err.message);
      throw err;
    }
  };

  return {
    user,
    profile,
    error,
    signIn,
    signUp,
    signOut,
    updateProfile,
    resetPassword
  };
}